/**
 * THE UNIT SYSTEM, AND THE ONLY TWO PLACES A NUMBER IS ALLOWED TO BE ROUNDED OR FORMATTED.
 *
 * ── WHY INTEGERS, STATED ONCE ──────────────────────────────────────────────────────────
 *
 * `BUILD_INSTRUCTIONS.md` §6.1: *"Use integer arithmetic on tenths (or a decimal type) to
 * avoid floating-point errors."* Migration `0091` convention 4 carries that into the schema:
 * every percentage is stored as an INTEGER NUMBER OF TENTHS of a percentage point, `1000` =
 * 100.0%.
 *
 * This is not a style preference, and the proof is already in the repository. A float engine
 * PASSES §6.2's headline assertion — `0.15*90 + 0.2*80 + 0.15*85 + 0.1*100 + 0.15*60 +
 * 0.15*50 + 0.1*70` lands on exactly `75.75` in IEEE 754, because every input happens to be
 * a multiple of 5. It then loses the last digit on the first set of one-decimal weights a
 * real client enters: `[54.7, 12.9, 21.2, 9.8, 0.2, 0.3, 0.9]` sums to
 * `100.00000000000001`, so a float `=== 100.0` balance check REFUSES A VALID MODEL, and
 * `33.3/33.3/33.4` against `90/60/30` misses `59.97` in both orderings.
 * `test/erp-consolidation/weighted-overlap.ts` §1 measures all three.
 *
 * ── THE UNITS ──────────────────────────────────────────────────────────────────────────
 *
 * | Quantity | Unit | 100% / one unit |
 * |---|---|---|
 * | An input percentage (weight, overlap, score, confidence, threshold) | TENTHS of a percentage point | `1000` |
 * | A computed percentage (every index this package returns) | TEN-THOUSANDTHS of a percentage point | `1_000_000` |
 * | Duration | HUNDREDTHS of a month (`estimate.duration_*_months` is `NUMERIC(6,2)`) | `100` = 1 month |
 * | Money | the currency's own integer minor unit (`NUMERIC(18,2)` → cents) | — |
 *
 * ── WHY TEN-THOUSANDTHS FOR RESULTS, WHICH IS THE OBSERVATION THAT MAKES IT EXACT ──────
 *
 *     weight%  = wT / 10          overlap%  = oT / 10
 *     WOS%     = Σ (weight%/100 × overlap%)
 *              = Σ (wT/1000 × oT/10)
 *              = Σ (wT × oT) / 10000
 *     WOS in ten-thousandths of a %  =  WOS% × 10000  =  Σ (wT × oT)
 *
 * **The exact answer is a plain integer sum of products — no division at all.** For §6.2's
 * worked example that sum is 757 500, and 757 500 ten-thousandths of a percentage point IS
 * 75.75%, exactly, by construction rather than by rounding. Every index in this package
 * (§6.3 complexity, §6.4 importance, §6.6 composite rating) is the same two-tenths product,
 * so the same unit makes all of them exact for the same reason.
 *
 * Where a division is genuinely unavoidable — renormalizing after N/A (§6.2 option B),
 * averaging, and three-point expected values (§6.7, which divides by 6) — it happens ONCE,
 * on integers, through `roundHalfUp`, and the result carries an `exact` flag saying whether
 * rounding was applied at all.
 *
 * ── PURITY ─────────────────────────────────────────────────────────────────────────────
 *
 * Nothing in this package reads a clock, a database, an environment variable or a request.
 * `tsconfig.json` sets `"types": []` so there is no `process` and no Node global to reach
 * for. §6.7's *"No dates anywhere"* is therefore structural here, not a convention: this
 * package never constructs a `Date`.
 */

/** 100.0% in the INPUT unit: tenths of a percentage point. */
export const ONE_HUNDRED_PCT_TENTHS = 1000;

/** 100.0000% in the RESULT unit: ten-thousandths of a percentage point. */
export const ONE_HUNDRED_PCT_TEN_THOUSANDTHS = 1_000_000;

/**
 * The constant denominator of every weight × value product in this package.
 *
 * `tenths × tenths = ten-thousandths × 10000`, so a sum of products over weights that
 * balance to `1000` is ALREADY in ten-thousandths and is divided by nothing. The constant
 * exists so the derivation is nameable where it is relied on, not so it is applied.
 */
export const TENTHS_PRODUCT_SCALE = 10_000;

/**
 * Integer division, rounding half AWAY FROM ZERO.
 *
 * Used only where the arithmetic genuinely does not divide out. `Math.round` is not a
 * substitute: it rounds half toward +Infinity, so it is asymmetric on negatives, and it
 * would be applied to a float that has already lost the exactness this package exists to
 * keep.
 *
 * Byte-identical in behaviour to `test/erp-consolidation/scoring-spec.ts`'s `roundHalfUp`,
 * which A8 wrote first as the executable specification. The two are cross-checked against
 * each other in `test/erp-consolidation/scoring-package.ts`.
 */
export function roundHalfUp(numerator: number, denominator: number): number {
  if (denominator === 0) throw new Error('roundHalfUp: division by zero');
  if (!Number.isInteger(numerator) || !Number.isInteger(denominator)) {
    throw new Error(`roundHalfUp: integers only, got ${numerator}/${denominator}`);
  }
  const sign = Math.sign(numerator) * Math.sign(denominator);
  const n = Math.abs(numerator);
  const d = Math.abs(denominator);
  return sign * Math.floor((2 * n + d) / (2 * d));
}

/**
 * Render a ten-thousandths value the way a report would: `757500` → `"75.75%"`.
 *
 * The ONE formatter for a computed percentage. §6.1's two sentences have their own
 * (`formatBalanceMessage`, which works in tenths because weights are entered in tenths).
 */
export function formatPct(tenThousandths: number, decimals = 2): string {
  const scale = 10 ** (4 - decimals);
  const rounded = roundHalfUp(tenThousandths, scale);
  return `${(rounded / 10 ** decimals).toFixed(decimals)}%`;
}

/** Guard for every integer input, so a float reaches no formula in this package. */
export function assertInteger(value: number, what: string): number {
  if (!Number.isInteger(value)) {
    throw new Error(`${what} must be an integer, got ${value}`);
  }
  return value;
}

/**
 * Guard for a percentage carried in tenths.
 *
 * The range is `0..1000`, NOT the DDL's `0 OR 10..1000`. The narrower CHECK exists to make a
 * stray 1-5 value unstorable (`0096`: *"a stray 1-5 value is still rejected"*), which is a
 * data-entry concern. A weight of `0.1%` through `0.9%` is legal arithmetic — `0094` allows
 * `weight_tenths BETWEEN 0 AND 1000` with no gap, and §6.1's *"Adding a capability gives it
 * 0.0"* requires `0` — so the package refuses only what is genuinely out of range and leaves
 * the stricter entry rule to the column that states it.
 */
export function assertPctTenths(value: number, what: string): number {
  assertInteger(value, what);
  if (value < 0 || value > ONE_HUNDRED_PCT_TENTHS) {
    throw new Error(`${what} out of range 0..${ONE_HUNDRED_PCT_TENTHS} tenths, got ${value}`);
  }
  return value;
}

/** Guard for a count that §6.3 bands: a non-negative integer. */
export function assertCount(value: number, what: string): number {
  assertInteger(value, what);
  if (value < 0) throw new Error(`${what} must be >= 0, got ${value}`);
  return value;
}

/**
 * Two factor weights that must sum to exactly 100.0%.
 *
 * §6.3's `0.4 / 0.6` and §6.4's `0.5 / 0.5`, both *"configurable"* and both carried in
 * `scoring_config` under a database CHECK that says the same thing
 * (`systems_factor_weight_tenths + interfaces_factor_weight_tenths = 1000`). Checked again
 * here because the package is also run by the client, where no CHECK exists.
 */
export function assertFactorPair(a: number, b: number, what: string): void {
  assertInteger(a, `${what} first factor`);
  assertInteger(b, `${what} second factor`);
  if (a + b !== ONE_HUNDRED_PCT_TENTHS) {
    throw new Error(
      `${what} factors must sum to ${ONE_HUNDRED_PCT_TENTHS} tenths (100.0%), got ${a + b}`,
    );
  }
}

/**
 * The exact mean of integers, with the rounding stated rather than hidden.
 *
 * Returns the numerator and denominator alongside the rounded mean so a caller that needs to
 * combine averages can do so on the exact numbers instead of on a rounded one — which is how
 * a roll-up of roll-ups loses a digit per level.
 */
export interface Mean {
  readonly mean: number;
  readonly numerator: number;
  readonly denominator: number;
  readonly exact: boolean;
}

/** The mean of an empty set is not 0; `count === 0` says so and `mean` is 0 only formally. */
export function meanOf(values: readonly number[]): Mean {
  const denominator = values.length;
  if (denominator === 0) return { mean: 0, numerator: 0, denominator: 0, exact: true };
  const numerator = values.reduce((a, b) => a + b, 0);
  return {
    mean: roundHalfUp(numerator, denominator),
    numerator,
    denominator,
    exact: numerator % denominator === 0,
  };
}
